<template>
    <div class="popup sink-selection" v-show="visible">
        <div class="content">
            <a href="" class="close icon-close" @click.prevent="close"></a>

            <h2 class="h2" v-html="ui.t('sinkChoose')"></h2>

            <label class="form-group big" v-if="ui.sinks && ui.sinks.options">
                <div class="image-group">
                    <a href="#" class="list-group-item col-2-4" v-for="sink in configuration.models.sinks.filter(s => s.productFamilies.includes(state.productFamily!))" :key="sink.code"
                        :class="getCssClasses(sink)" @click.prevent="setSink(sink)">

                        <div class="container">
                            <div class="top">
                                <div class="image" v-if="sink.image" :style="{ 'background-image': ui.getBackgroundImageUrl(sink.image, 200)! }"></div>

                                <h3>
                                    {{ sink.name }}
                                </h3>
                            </div>

                            <div class="messages" v-if="getMessages(sink).length > 0">
                                <div v-for="message in getMessages(sink)" :key="message" class="message" v-html="ui.t(message)"></div>
                            </div>
                        </div>
                    </a>
                    <a v-if="state.productFamily == 'seven'" href="#" class="list-group-item col-2-4" v-for="sink in configuration.models.sinks.filter(s => ['K', 'M'].includes(s.code))" :key="sink.code"
                        :class="getCssClasses(sink)" @click.prevent="jumpToProductFamily('one')">

                        <div class="container">
                            <div class="top">
                                <div class="image" v-if="sink.image" :style="{ 'background-image': ui.getBackgroundImageUrl(sink.image, 200)! }"></div>

                                <h3>
                                   {{ ui.t("oneSinksCrosslinkText", sink.code, `${sink.length}x${sink.width}`) }}
                                </h3>
                            </div>

                            <div class="messages" v-if="getMessages(sink).length > 0">
                                <div v-for="message in getMessages(sink)" :key="message" class="message" v-html="ui.t(message)"></div>
                            </div>
                        </div>
                    </a>
                </div>
            </label>
        </div>
        <confirm-popup ref="confirmPopup" :title="ui.t('confirmChangeProductFamilyTitle')" :content="ui.t('crosslinkMaterialWillBeRemoved')"></confirm-popup>
    </div>
</template>

<script async setup lang="ts">
import { useRouter } from 'vue-router';
import { useConfiguration, useState, useUI } from '../store';
import type { ConfigurationOption } from '../models';
import { runExpr } from '../expr';
import { ref } from 'vue';
import ConfirmPopup from './ConfirmPopup.vue';


const { visible = false } = defineProps<{ visible: boolean }>();

const emit = defineEmits(["update:visible"]);

const { state } = useState();
const { ui } = useUI();
const router = useRouter();
const { configuration } = useConfiguration();

const confirmPopup = ref<InstanceType<typeof ConfirmPopup>>();
const familyChanged = ref(false);

async function jumpToProductFamily(familyCode: string) {
    const selectedMaterial = configuration.models.materials.find(m => m.code == state.material);
    let needsMaterialSelection = false;

    // If target family does not have chosen material, show confirm and remove material
    if (!selectedMaterial || !selectedMaterial.productFamilies.includes(familyCode)) {
        if (!await confirmPopup.value?.confirm())
            return;

        state.material = null;
        state.graphics = null;
        needsMaterialSelection = true;
    }
    // Otherwise material is compatible, delay backstyle notification until popup is closed
    else {
        familyChanged.value = true;
    }

    state.setFamilyWithoutReset(familyCode);

    if (state.vertex && familyCode == "one")
        state.disableAccessories = true;
    else if (state.vertex && familyCode == "seven")
        state.disableAccessories = false;

    // If material was removed, redirect to selection
    if (needsMaterialSelection) {
        // Notify user about new backstyle option
        if (familyCode == "one") 
            ui.showNewWorktopOptionsAvailable();
        router.push(`/${state.productFamilyCode}/material`);
    }
}

function setSink(sink: ConfigurationOption) {
    if (!isActive(sink))
        return;

    if (familyChanged.value) {
        ui.showNewWorktopOptionsAvailable();
        familyChanged.value = false;
    }

    let sinkWasNull = state.sink == null;

    let hobcutoutDistanceLeft = ui.hobcutoutDistanceLeft;

    let positionNeedsUpdate = !sinkWasNull && state.sinkHandedness != sink.handedness;

    state.sink = sink.code;

    state.sinkHandedness = sink.handedness;

    state.sinkDistanceX = null;
    state.faucetHole = "0";
    state.extraHoleCode = "0";

    if (positionNeedsUpdate && state.hobcutout == "1") {
        //ui.sinkDistanceLeft = sinkDistanceLeft;
        ui.hobcutoutDistanceLeft = hobcutoutDistanceLeft;
    }

    emit("update:visible", false);

    //Skip sink step if user has chosen "no sink"
    if (sinkWasNull && !ui.sink.hasDimensions)
        router.push(`/${state.productFamilyCode}/hobcutout`);
}

function getMessages(sink: ConfigurationOption): string[] {
    let messages = [];

    if (sink.rules) {
        for (let rule of sink.rules) {
            let enabled = runExpr({
                state: state,
                ui: ui,
                selfKey: "sink"
            }, rule.expression);

            if (!enabled && messages.indexOf(rule.resourceKey) == -1) {
                messages.push(rule.resourceKey);
            }
        }
    }

    return messages;
}

function isActive(sink: ConfigurationOption): boolean {
    return getMessages(sink).length == 0;
}

function getCssClasses(sink: ConfigurationOption) {
    let selectedSinks = configuration.models.sinks.filter(s => s.code == sink.code);

    return {
        active: state.sink == sink.code && (state.sinkHandedness == sink.handedness || selectedSinks.length == 1),
        disabled: !isActive(sink) || !sink.productFamilies.includes(state.productFamily ?? ""),
        crosslink: !sink.productFamilies.includes(state.productFamily ?? "")
    };
}

function close() {
    emit("update:visible", false);
}

</script>
